import { History } from "lucide-react";
import React from "react";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useFormatting } from "@/hooks/useFormatting";
import type { ImpactHistoryEntry } from "@/types/scenarios";

interface ImpactHistoryIconProps {
  history: ImpactHistoryEntry[];
  /** Title shown in the modal */
  title?: string;
}

function formatDateTime(date: Date): string {
  return date.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export const ImpactHistoryIcon: React.FC<ImpactHistoryIconProps> = ({
  history,
  title = "Impact History",
}) => {
  const [open, setOpen] = React.useState(false);
  const { formatCurrency } = useFormatting();

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(true);
              }}
              className="inline-flex items-center justify-center h-5 w-5 rounded text-gray-400 hover:text-teal-600 hover:bg-teal-50 transition-colors flex-shrink-0"
              aria-label="View impact history"
            >
              <History className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p>View impact history</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>

          {history.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center">
              No changes since first calculated.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date / Time</TableHead>
                  <TableHead>Refresh Type</TableHead>
                  <TableHead>Triggered By</TableHead>
                  <TableHead className="text-right">Previous Value</TableHead>
                  <TableHead className="text-right">New Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((entry) => {
                  const delta = entry.newValue - entry.previousValue;
                  const isIncrease = delta > 0;
                  return (
                    <TableRow key={entry.id}>
                      <TableCell className="text-sm whitespace-nowrap">
                        {formatDateTime(entry.dateTime)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            entry.refreshType === "Scheduled"
                              ? "secondary"
                              : "outline"
                          }
                          className="text-xs"
                        >
                          {entry.refreshType}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {entry.triggeredBy}
                      </TableCell>
                      <TableCell className="text-right text-sm font-mono">
                        {formatCurrency(entry.previousValue, "full")}
                      </TableCell>
                      <TableCell className="text-right text-sm font-mono">
                        <span
                          className={
                            isIncrease
                              ? "text-green-700"
                              : delta < 0
                                ? "text-red-700"
                                : "text-gray-700"
                          }
                        >
                          {formatCurrency(entry.newValue, "full")}
                        </span>
                        {delta !== 0 && (
                          <span
                            className={`ml-1.5 text-xs ${isIncrease ? "text-green-600" : "text-red-600"}`}
                          >
                            ({isIncrease ? "+" : ""}
                            {formatCurrency(delta, "full")})
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
